import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LoadingService } from '@services/loading.service';
import { standardGetHttpOptions, standardHttpOptions, UserAccessRole, UserGroup, UserInfos } from '@models';
import { Observable } from 'rxjs';
import { throwError } from 'rxjs';
import { KigSettings, KIG_SETTINGS } from '../../../settings/kig-settings';
import { GraphUser } from '@models';
import { catchError, finalize, map } from 'rxjs/operators';
import { GraphApiService } from './graph-api.service';
import { of } from 'rxjs';
import { UserInformation, UserInformationService } from '@corteva-research/ngx-components-core';
import { CommonShared } from '../classes/common';


@Injectable()

export class UserService {

  private apiUrl: string;
  public common = new CommonShared();

  // Cached variables
  public allUserGroups: UserGroup[] = [];
  private associatedUserGroupsByUser = new Map<string, UserGroup[]>();
  public graphUser: GraphUser[];

  
  constructor(private http: HttpClient, @Inject(KIG_SETTINGS) private settings: KigSettings,
              private loadingService: LoadingService, private graphApiService: GraphApiService) {
    this.apiUrl = this.settings.apiUrl;
  }


  public clearCachedVariables() {
    this.allUserGroups = [];
    this.associatedUserGroupsByUser.clear();
  }


  public getUserByLastNameAzureAD(lastName: string): Observable<GraphUser[]> {
    const result =  this.graphApiService.getUsersByLastName(lastName);
    return result;
  }


  public getCurrentUserEmailUsingAzureAD(): Observable<GraphUser[]> {
    if (this.graphUser != null) {
      //console.log('cached graph user');
      return new Observable<GraphUser[]>((obs) => {
        obs.next(this.graphUser);
        obs.complete(); 
      });
    }

    return this.graphApiService.getCurrentUserEmailAddress().pipe(
      map(value => {
        this.graphUser = value;

        // // Using the following code to temporarily replace email domain to corteva.com during separation year
        // if (this.graphUser['value']) {
        //   this.graphUser['value'] = this.graphUser['value'].replace(/@[^@]+$/, '@corteva.com');
        // }

        return this.graphUser;
      })
    );
  }


  public getUserGroups(): Observable<any> {
    if (this.allUserGroups.length > 0) {
      return new Observable<UserGroup[]>((obs) => {
        obs.next(this.allUserGroups);
        obs.complete(); 
      });
    }

    this.loadingService.setMessage('Loading User Groups ...');
    return this.http.get(`${ this.apiUrl }/usergroup`, standardGetHttpOptions).pipe(
      map((response: UserGroup[]) => {
        this.allUserGroups = response;
        this.common.sortArrayByNameParm(this.allUserGroups);
        this.allUserGroups.forEach(g => this.updateUserInfoRoles(g) );
        return this.allUserGroups;
      }),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  public getAssociatedUserGroups(username: string): Observable<any> {
    // The cache is per user; callers get copies because some pages set fields (e.g. status) on the groups they receive
    const cacheKey = (username ?? '').toLowerCase();
    const cached = this.associatedUserGroupsByUser.get(cacheKey);
    if (cached != null) {
      return of(cached.map(g => ({ ...g })));
    }

    this.loadingService.setMessage('Loading Associated User Groups ...');
    return this.http.get(`${ this.apiUrl }/user/associatedusergroups/${ username }`, standardGetHttpOptions).pipe(
      map((response: UserGroup[]) => {
        const groups = response;
        this.common.sortArrayByNameParm(groups);
        groups.push(
          {
            id: -1,
            createdDate: new Date(),
            description: 'self',
            isdisabled: false,
            lastModifiedDate: new Date(),
            name: 'Self',
            status: 'Active',
            userInfos: []
          });
        this.associatedUserGroupsByUser.set(cacheKey, groups);
        return groups.map(g => ({ ...g }));
      }),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  public getAssociatedItems(username: string): Observable<any> {
    this.loadingService.setMessage('Loading Associated Items ...');
    return this.http.get(`${ this.apiUrl }/user/associatedItems/${ username }`, standardGetHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  public getAllUsers(): Observable<any> {
    this.loadingService.setMessage('Loading Users ...');
    return this.http.get(`${ this.apiUrl }/user`, standardGetHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }
  

  public getAllUsersWithStatus(): Observable<any> {
    this.loadingService.setMessage('Loading Users ...');
    return this.http.get(`${ this.apiUrl }/user/withStatus`, standardGetHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  public getUserRole(userName: string): Observable<any> {
    this.loadingService.setMessage('Getting user\'s role ...');
    return this.http.get(`${ this.apiUrl }/user/role/${ userName }`, standardGetHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  public updateUsers(userInfos: UserInfos[]) {
    let body = JSON.stringify(userInfos);
    this.loadingService.setMessage('Updating users ...');
    return this.http.put(`${this.apiUrl}/user/`, body, standardHttpOptions).pipe(
      map((response: Response) => {
        if (response == null) {
          return 'success';
        }
        return response;
      }),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  public getUsersByLastName(lastName: string): Observable<any> {
    this.loadingService.setMessage('Loading Users...');
    return this.http.get(`${ this.apiUrl }/activedirectory/${ lastName }`, standardGetHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  public createUserGroup(userGroup: UserGroup) {
    this.loadingService.setMessage('Creating User Group...');
    return this.http.post(`${ this.apiUrl }/usergroup/`, JSON.stringify(userGroup), standardHttpOptions).pipe(
      map((response: Response) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  public deleteUserGroup(id: number) {
    this.loadingService.setMessage('Deleting User Group...');
    return this.http.delete(`${ this.apiUrl }/usergroup/${ id }`, standardHttpOptions).pipe(
      map((response: Response) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  public updateUserGroup(userGroup: UserGroup) {
    const body = JSON.stringify(userGroup);
    this.loadingService.setMessage('Updating user group...');
    return this.http.put(`${this.apiUrl}/usergroup/`, body, standardHttpOptions).pipe(
      map((response: Response) => {
        if (response == null) {
          return 'success';
        }
        return response;
      }),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }

  
  public replaceUserOwnedItemsWithGroup(userId: number, userGroupId: number) {
    this.loadingService.setMessage('Updating user details ...');
    return this.http.put(`${this.apiUrl}/user/UpdateUserOwnedItemsWithGroup/${ userId }/${ userGroupId }`, standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  // Quiet, fire-and-forget: stamps the current user's last site-load timestamp on browser load.
  // No loading spinner since it runs on every full app bootstrap.
  public updateLastSiteLoadDate(username: string) {
    return this.http.put(`${this.apiUrl}/user/lastSiteLoad/${ encodeURIComponent(username) }`, null, standardHttpOptions).pipe(
      catchError(this.handleError));
  }


  public deleteUser(userId: number) {
    this.loadingService.setMessage('Deleting User ...');
    return this.http.delete(`${ this.apiUrl }/user/${ userId }`, standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  private handleError(error: any) {
    if (error.status === 404) {
      return of(null);
    }
    return throwError(() => error);
  }

  /////////////////

    updateUserInfoRoles(userGroup: UserGroup) {
      if (userGroup.userInfos == undefined) {
        userGroup.userInfos = [];
        return;
      };
  
      for (var index = 0; index < userGroup.userInfos.length; index++) {
        var user = userGroup.userInfos[index];
        if (user.role == undefined || Object.values(UserAccessRole)[user.role] == UserAccessRole.Unassigned) {
          userGroup.userInfos[index].role = UserAccessRole.GroupFullRW;
        }
        else {
          userGroup.userInfos[index].role = Object.values(UserAccessRole)[user.role];
        }
      }
    }

    
}
